import { useFusionAuth } from "@fusionauth/react-sdk";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Alert,
  AlertTitle,
  Badge,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWindowQueryParams } from "../../services/hooks/useQueryParams";
import ProjectActivity from "../activity/ProjectActivity";
import CustomDialog from "../CustomDialog.tsx";
import { resetToDefaultView } from "../datagrid/ToolbarViewMenuUtils.tsx";
import DataExportDrawer from "./DataExportDrawer";
import DetailsModal from "./DetailsModal";
import FilterDataSet from "./filterdataset/FilterDataSet";

interface ProjectHeaderInput {
  project: ProjectContextType;
  selectedTab: number;
  setSelectedTab: (value: number) => void;
  refreshData: () => void;
  disabled: boolean;
  disabledIcon?: unknown;
  patientsLoading: boolean;
  patientsError: boolean;
  disableRefineButton: boolean;
  showCodeUpdateFailureAlert: boolean;
  showPollingTimeoutAlert: boolean;
  forceTableUpdate: () => void;
}

export default function ProjectHeader({
  project,
  selectedTab,
  setSelectedTab,
  refreshData,
  disabled,
  disabledIcon,
  patientsLoading,
  patientsError,
  disableRefineButton,
  showCodeUpdateFailureAlert,
  showPollingTimeoutAlert,
  forceTableUpdate,
}: ProjectHeaderInput) {
  const { deleteWindowQueryParam } = useWindowQueryParams();
  const { userInfo } = useFusionAuth();

  const [markButton, setMarkButton] = useState<
    (EventTarget & HTMLButtonElement) | undefined
  >();
  const navigate = useNavigate();
  const [exportDrawer, setExportDrawer] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFilterDataSet, setShowFilterDataSet] = useState(false);
  const [showProjectActivity, setShowProjectActivity] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  const goProjects = () => {
    navigate("/");
  };

  function handleTabChange(
    _event: React.SyntheticEvent<Element, Event>,
    value: number,
  ) {
    setSelectedTab(value);
    if (value === 0) {
      deleteWindowQueryParam("patientId");
    }
  }

  function handleDetailsInfo(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) {
    setMarkButton(event.currentTarget);
    setShowDetailsModal(true);
  }

  function handleDetailsInfoClose() {
    setShowDetailsModal(false);
  }

  function handleProjectActivity() {
    setShowProjectActivity(true);
    setShowDetailsModal(false);
  }

  function onCloseProjectActivity() {
    setShowProjectActivity(false);
  }

  function handleOpenFilterData() {
    setShowFilterDataSet(true);
  }

  function onCloseFilterData(options?: { fetchProject?: boolean }) {
    const { fetchProject } = options || {};
    setShowFilterDataSet(false);
    if (fetchProject) {
      refreshData();
    }
  }

  function handleExport(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) {
    setMarkButton(event.currentTarget);
    setExportDrawer(true);
  }

  function closeExportDrawer() {
    setExportDrawer(false);
  }

  function handleResetDatagridViews() {
    // clear localstorage for all items starting with userId - projectID
    Object.keys(localStorage)
      .filter(
        (key) =>
          key.includes(`user:${userInfo?.sub}`) &&
          key.endsWith(`project:${project.id}`),
      )
      .forEach((key) => resetToDefaultView(key));

    setShowResetModal(false);
    // forces currently loaded patient tab tables to reset
    forceTableUpdate();
  }

  return (
    <div className="details-body">
      <Paper className="details-box" elevation={0} square>
        <Stack direction={"column"} spacing={1} className="details-box-left">
          <Stack
            direction={"row"}
            spacing={1}
            className={"details-box-left-top"}
            alignItems={"center"}
          >
            <IconButton
              onClick={goProjects}
              sx={{ "&:hover": { bgcolor: "action.hover", height: "auto" } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">{project.name}</Typography>
            {!disabledIcon && (
              <IconButton
                onClick={handleDetailsInfo}
                sx={{ "&:hover": { bgcolor: "action.hover" } }}
              >
                <InfoOutlinedIcon />
              </IconButton>
            )}
          </Stack>
          <Stack direction={"row"} spacing={1}>
            <Box>
              <Tabs value={selectedTab} onChange={handleTabChange} centered>
                <Tab className={"tab"} label={"Overview"} />
                {patientsLoading ? (
                  <Tab
                    className={"tab"}
                    label={`Patients`}
                    icon={<CircularProgress color={"inherit"} size={".7rem"} />}
                    iconPosition={"end"}
                    disabled={true}
                  />
                ) : (
                  <Tab
                    className={"tab"}
                    label={
                      patientsError ? `Error Loading Patients` : `Patients`
                    }
                    disabled={patientsError ? true : disabled}
                  />
                )}
              </Tabs>
            </Box>
          </Stack>
        </Stack>
        <Stack direction={"row"} spacing={1} className={"details-box-right"}>
          <Button
            variant={"outlined"}
            className={"filter-button"}
            onClick={() => setShowResetModal(true)}
            startIcon={<ReplayIcon />}
          >
            Reset Data Views
          </Button>
          <Button
            variant={"outlined"}
            className={"filter-button"}
            onClick={handleOpenFilterData}
            disabled={disableRefineButton}
          >
            Refine Data Set
          </Button>
          <Button
            id="data-export"
            variant={"outlined"}
            className={"filter-button"}
            onClick={handleExport}
            disabled={disabled}
          >
            Generate Data Files
          </Button>
        </Stack>
      </Paper>
      {showDetailsModal && (
        <DetailsModal
          anchorEl={markButton}
          onClose={handleDetailsInfoClose}
          onProjectActivity={handleProjectActivity}
          project={project}
        />
      )}
      {showFilterDataSet && (
        <FilterDataSet onClose={onCloseFilterData} project={project} />
      )}
      {showProjectActivity && (
        <ProjectActivity onClose={onCloseProjectActivity} />
      )}
      {exportDrawer && (
        <DataExportDrawer onClose={closeExportDrawer} anchorEl={markButton} />
      )}
      {showCodeUpdateFailureAlert && (
        <Alert className="prioritization-info-text " severity="error">
          <AlertTitle>
            Project Diagnosis & Procedure Code Loading Error
          </AlertTitle>
          Error Loading Data Set, Refine Data Set Disabled
        </Alert>
      )}
      {showPollingTimeoutAlert && (
        <Alert className="prioritization-info-text " severity="error">
          <AlertTitle>Project Data Loading</AlertTitle>
          Project data is still loading, please check back in a bit.
        </Alert>
      )}
      <CustomDialog
        open={showResetModal}
        title={"Reset all data views in this project?"}
        handleClose={() => setShowResetModal(false)}
        hasConfirmation
        confirmationText={`RESET ALL VIEWS IN THIS PROJECT`}
        handleConfirm={handleResetDatagridViews}
      >
        <>
          <Typography>
            This <b>will</b> reset all data views in this project back to the
            default view.
          </Typography>
          <Typography>
            This <b>will</b> delete any unsaved changes to current data views in
            this project, indicated by a&emsp;
            <Badge variant="dot" color="warning" />
            &emsp;in the toolbar.
          </Typography>
          <br />
          <Typography>
            This <b>will not</b> affect custom data views in this project.
          </Typography>
          <Typography>
            This <b>will not</b> affect data views in other projects.
          </Typography>
          <Typography>
            This <b>will not</b> affect data views for other users.
          </Typography>
        </>
      </CustomDialog>
    </div>
  );
}
